import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Home, Truck, Store, Heart, Users, Brain, Briefcase, HandHeart, MapPin, Sparkles, ArrowRight, Play, ChevronRight, Quote, Target, Shield, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import VillageOfHopeSignup from '../components/VillageOfHopeSignup';

const GIVEBUTTER_URL = "https://givebutter.com/XT2qPn";

const UpcomingInitiativesPage = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  const handleDonate = () => {
    window.open(GIVEBUTTER_URL, '_blank', 'noopener,noreferrer');
  };

  const communityFeatures = [
    {
      icon: Home,
      title: 'Tiny Home Village',
      description: 'A village of 25+ custom tiny homes—not shelters, but real homes with dignity. Each residence provides veterans and families a stable foundation to rebuild their lives with privacy, independence, and pride.',
      highlight: '25+ Homes',
    },
    {
      icon: Truck,
      title: 'Mobile Kitchen Fleet',
      description: 'Food trucks that do double duty: providing nutritious meals to our community while creating real culinary careers. Residents learn to cook, manage inventory, and run a business.',
      highlight: 'Jobs + Meals',
    },
    {
      icon: Store,
      title: 'Village General Store',
      description: 'A community marketplace offering essentials at fair prices, staffed entirely by residents gaining retail experience. Every purchase supports the mission.',
      highlight: 'Self-Sustaining',
    },
    {
      icon: Heart,
      title: 'Restoration Center',
      description: 'The heart of our village—a central hub where residents connect with case managers, access benefits, receive healthcare referrals, and find crisis support 24/7.',
      highlight: '24/7 Support',
    },
    {
      icon: Brain,
      title: 'Healing & Wellness',
      description: 'On-site licensed counselors, PTSD specialists, addiction recovery programs, and peer support groups. We address the invisible wounds that keep people trapped.',
      highlight: 'Trauma-Informed',
    },
    {
      icon: Briefcase,
      title: 'Career Launchpad',
      description: 'Vocational training in construction, culinary arts, retail, landscaping, and more. Plus resume building, interview coaching, and direct job placement with partner employers.',
      highlight: '90% Placement Goal',
    },
  ];

  const impactNumbers = [
    { value: '25+', label: 'Tiny Homes', sublabel: 'Safe, dignified housing' },
    { value: '100+', label: 'Lives Transformed', sublabel: 'Veterans & families' },
    { value: '50+', label: 'Volunteer Mentors', sublabel: 'Walking alongside' },
    { value: '∞', label: 'Generational Impact', sublabel: 'Breaking cycles' },
  ];

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="upcoming-initiatives-page">
      
      {/* HERO - Full Screen Immersive */}
      <section className="min-h-screen relative flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=2000&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1120]/80 via-[#0B1120]/70 to-[#0B1120]/90" />
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            {/* Badge */}
            <motion.div 
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
              The Vision That Will Change Everything
            </motion.div>
            
            {/* Main Headline */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium text-white tracking-tight leading-[1.05] mb-8">
              We're Building
              <br />
              <span className="bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400 bg-clip-text text-transparent">
                A Village of Hope
              </span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl sm:text-2xl text-white/80 font-light max-w-3xl mx-auto mb-12 leading-relaxed">
              Land. Tiny homes. Food trucks. A store. A help center. Mental health services. 
              Job training. Mentorship. <span className="text-white font-medium">All powered by love.</span>
            </p>

            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Button
                variant="outline"
                onClick={() => document.querySelector('#the-vision')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-7 text-lg rounded-full backdrop-blur-sm"
              >
                See The Vision
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Scroll Indicator */}
            <motion.div
              className="absolute bottom-12 left-1/2 -translate-x-1/2"
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            >
              <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
                <motion.div 
                  className="w-1.5 h-1.5 rounded-full bg-white"
                  animate={{ y: [0, 12, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* THE DREAM - Emotional Story Section */}
      <section id="the-vision" className="py-24 sm:py-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-600 mb-4">
                — The Dream —
              </p>
              <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] mb-8 leading-tight">
                Imagine a place where
                <span className="block text-[#1E3A8A]">broken becomes beautiful</span>
              </h2>
              
              <div className="space-y-6 text-lg text-slate-600 font-light leading-relaxed">
                <p>
                  Picture this: A veteran who served three tours, now sleeping in his car, 
                  finally has a <strong className="text-slate-800">home of his own</strong>. 
                  A single mother, fleeing an impossible situation, wakes up in a safe place 
                  where her children can laugh again.
                </p>
                <p>
                  This isn't a shelter. It's not a handout. It's a <strong className="text-slate-800">community</strong>—
                  a place where people rediscover their worth, learn new skills, heal old wounds, 
                  and get back on their feet with dignity.
                </p>
                <p>
                  <strong className="text-slate-800">We're going to build it.</strong> And we need you.
                </p>
              </div>

              {/* Quote */}
              <div className="mt-10 p-6 bg-slate-50 border-l-4 border-amber-500 rounded-r-xl">
                <Quote className="w-8 h-8 text-amber-500/50 mb-3" />
                <p className="text-xl text-slate-700 italic font-serif leading-relaxed">
                  "The most selfish thing you can do is help someone else, because the 
                  gratification that comes from helping others—nothing is better than that."
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              {/* Image Stack */}
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full bg-amber-500/20 rounded-3xl" />
                <div className="absolute -bottom-4 -right-4 w-full h-full bg-[#1E3A8A]/20 rounded-3xl" />
                <img
                  src="https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=800&q=80"
                  alt="Helping hands"
                  className="relative rounded-3xl shadow-2xl w-full"
                />
                
                {/* Floating Stats Card */}
                <motion.div 
                  className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl p-6 border border-slate-100"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <p className="text-4xl font-bold text-[#1E3A8A] font-serif">100+</p>
                  <p className="text-slate-600 text-sm">Lives we'll transform</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* IMPACT NUMBERS - Full Width */}
      <section className="py-20 bg-[#0B1120]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {impactNumbers.map((stat, idx) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <p className="text-5xl sm:text-6xl lg:text-7xl font-bold text-amber-400 font-serif mb-2">
                  {stat.value}
                </p>
                <p className="text-white font-medium text-lg mb-1">{stat.label}</p>
                <p className="text-slate-400 text-sm">{stat.sublabel}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE'RE BUILDING - Interactive Feature Showcase */}
      <section className="py-24 sm:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#1E3A8A] mb-4">
              — What We're Building —
            </p>
            <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] mb-6">
              A Complete Ecosystem of <span className="text-[#1E3A8A]">Restoration</span>
            </h2>
            <p className="text-xl text-slate-600 font-light max-w-3xl mx-auto">
              Every element intentionally designed to support healing, create opportunity, 
              and build lasting independence.
            </p>
          </motion.div>

          {/* Feature Cards - Large Format */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {communityFeatures.map((feature, idx) => (
              <motion.div
                key={feature.title}
                className="group relative bg-white rounded-3xl p-8 border border-slate-200 hover:border-[#1E3A8A]/30 hover:shadow-2xl transition-all duration-500 overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                {/* Highlight Badge */}
                <div className="absolute top-6 right-6">
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                    {feature.highlight}
                  </span>
                </div>
                
                {/* Icon */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#1E3A8A]/80 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-2xl font-semibold text-slate-800 mb-4 font-serif">
                  {feature.title}
                </h3>
                <p className="text-slate-600 font-light leading-relaxed">
                  {feature.description}
                </p>
                
                {/* Hover Effect */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-[#1E3A8A] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* THE LAND - Visual Focus */}
      <section className="py-24 sm:py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <div className="aspect-square relative rounded-3xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1000&q=80"
                  alt="Open land at sunrise"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120]/60 to-transparent" />
                
                {/* Overlay Content */}
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin className="w-5 h-5 text-amber-400" />
                    <span className="text-white/80 text-sm">Location: To Be Determined</span>
                  </div>
                  <p className="text-white text-2xl font-serif">
                    The land where dreams will take root
                  </p>
                </div>
              </div>
              
              {/* Corner Accents */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-4 border-l-4 border-amber-400 rounded-tl-3xl" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-4 border-r-4 border-[#1E3A8A] rounded-br-3xl" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-amber-600 mb-4">
                — Phase One —
              </p>
              <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] mb-8 leading-tight">
                First, we need
                <span className="block text-[#1E3A8A]">the right land</span>
              </h2>
              
              <p className="text-lg text-slate-600 font-light mb-8 leading-relaxed">
                We're searching for the perfect property—acreage with room to grow, space for 
                gardens, areas for community gathering, and the potential to become something 
                truly special.
              </p>

              <div className="space-y-4 mb-10">
                {[
                  { icon: Home, text: 'Space for 25+ tiny homes' },
                  { icon: Leaf, text: 'Land for community gardens' },
                  { icon: Users, text: 'Central gathering spaces' },
                  { icon: Target, text: 'Room for future expansion' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-[#1E3A8A]" />
                    </div>
                    <span className="text-slate-700 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              <Button
                onClick={() => document.querySelector('#village-signup')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-8 py-6 text-lg rounded-full"
              >
                Get Involved
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* POWERED BY LOVE - Volunteer Section */}
      <section className="py-24 sm:py-32 bg-gradient-to-br from-[#0B1120] via-[#0B1120] to-[#1E3A8A]/50 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <HandHeart className="w-16 h-16 text-amber-400 mb-8" />
              <h2 className="text-4xl sm:text-5xl font-medium mb-8 leading-tight">
                Powered by <span className="text-amber-400">Love</span>,
                <br />Driven by <span className="text-amber-400">Purpose</span>
              </h2>
              
              <div className="space-y-6 text-lg text-slate-300 font-light leading-relaxed">
                <p>
                  At the heart of this village will be <strong className="text-white">volunteers</strong>—
                  not just people who show up, but mentors who walk alongside residents. 
                  People with a spirit of <strong className="text-white">discipleship</strong> who understand 
                  that real transformation happens through relationship.
                </p>
                <p>
                  We're looking for mentors, counselors, teachers, cooks, builders, 
                  and anyone with a heart to help others find their way back to hope.
                </p>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => { navigate('/'); setTimeout(() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                  className="bg-amber-500 hover:bg-amber-400 text-[#0B1120] font-semibold px-8 py-6 text-lg rounded-full"
                >
                  Become a Volunteer
                </Button>
                <Button
                  onClick={() => { navigate('/'); setTimeout(() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full"
                >
                  Partner With Us
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { icon: Users, label: 'Mentors', value: '50+', desc: 'Needed' },
                { icon: Home, label: 'Tiny Homes', value: '25+', desc: 'Planned' },
                { icon: Heart, label: 'Lives', value: '100+', desc: 'Transformed' },
                { icon: Shield, label: 'Veterans', value: 'Priority', desc: 'Focus' },
              ].map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <stat.icon className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white font-serif mb-1">{stat.value}</p>
                  <p className="text-white/60 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE ROADMAP - Timeline */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#1E3A8A] mb-4">
              — The Roadmap —
            </p>
            <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] mb-6">
              How We'll <span className="text-[#1E3A8A]">Make It Happen</span>
            </h2>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-400 via-[#1E3A8A] to-slate-200" />

            {[
              {
                phase: 'Phase 1',
                title: 'Land Acquisition',
                description: 'Secure the perfect property with space for our entire village, gardens, and community facilities. This is where it all begins.',
                status: 'NOW',
                statusColor: 'bg-amber-500',
              },
              {
                phase: 'Phase 2',
                title: 'Foundation & First Homes',
                description: 'Develop infrastructure—water, power, roads. Build the first 10 tiny homes and welcome our first residents home.',
                status: 'NEXT',
                statusColor: 'bg-[#1E3A8A]',
              },
              {
                phase: 'Phase 3',
                title: 'Support Services Launch',
                description: 'Open the Restoration Center, launch food service operations, bring in counselors, and start job training programs.',
                status: 'PLANNED',
                statusColor: 'bg-slate-400',
              },
              {
                phase: 'Phase 4',
                title: 'Full Village Operation',
                description: 'Expand to 25+ homes, open the community store, achieve self-sustainability, and become a model for others to replicate.',
                status: 'VISION',
                statusColor: 'bg-slate-300',
              },
            ].map((item, idx) => (
              <motion.div
                key={item.phase}
                className="relative pl-24 pb-16 last:pb-0"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
              >
                {/* Circle */}
                <div className={`absolute left-4 w-8 h-8 rounded-full ${item.statusColor} flex items-center justify-center ring-4 ring-white shadow-lg`}>
                  <span className="text-white text-xs font-bold">{idx + 1}</span>
                </div>

                {/* Content */}
                <div className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-xs font-bold tracking-wider uppercase text-slate-400">{item.phase}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${item.statusColor}`}>
                      {item.status}
                    </span>
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-800 mb-3 font-serif">{item.title}</h3>
                  <p className="text-slate-600 font-light leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Village of Hope Volunteer Signup */}
      <VillageOfHopeSignup />

      {/* FINAL CTA - Emotional Close */}
      <section className="py-24 sm:py-32 bg-gradient-to-br from-slate-50 to-slate-100 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 lg:px-24 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-amber-500/30">
              <Heart className="w-12 h-12 text-white" />
            </div>
            
            <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] mb-6 leading-tight">
              This Dream Is <span className="text-[#1E3A8A]">Possible</span>
              <br />With You
            </h2>
            
            <p className="text-xl text-slate-600 font-light mb-10 max-w-2xl mx-auto leading-relaxed">
              Every great movement started with people who said "yes" before they could see the full picture. 
              Your time, your skills, your prayers—they all matter. 
              <strong className="text-slate-800"> Will you help us build this village?</strong>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => document.querySelector('#village-signup')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-12 py-7 text-xl rounded-full shadow-xl hover:shadow-2xl transition-all"
              >
                <Heart className="w-6 h-6 mr-2" />
                Yes, I'll Help Build This
              </Button>
            </div>

            <p className="mt-8 text-slate-500 text-sm">
              The Prestigious Travel Club Foundation is a registered 501(c)(3).
            </p>
          </motion.div>
        </div>

        {/* Background Decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#1E3A8A]/10 rounded-full blur-3xl" />
      </section>
    </div>
  );
};

export default UpcomingInitiativesPage;
